import i18nFactory from '@yandex-int/i18n';
import { Spin } from 'lego-on-react';
import * as React from 'react';
import { MouseEvent, ReactNode } from 'react';

import { Bem } from '../../../service/Bem/Bem';
import { Alert, AlertSize, AlertType } from '../../Alert/Alert';
import {
    Shape2,
    Shape2Icon,
    Shape2Theme,
    Shape2Tone
} from '../../Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../../StyledLink/StyledLink';

import './HeaderExpandedInfoBlock.css';
import * as keyset from './HeaderExpandedInfoBlock.i18n';

const i18n = i18nFactory(keyset);

interface HeaderExpandedInfoBlockProps {
    title: string;
    isOpen: boolean;
    onClick: () => void;
    moreInfoHref?: string;
    children: ReactNode;
    fetchItems: () => void;
    isLoading?: boolean;
    error?: string;
}

export class HeaderExpandedInfoBlock extends React.PureComponent<
    HeaderExpandedInfoBlockProps
> {
    private content: ReactNode | null;
    private isFetching: boolean = false;

    componentDidUpdate(prevProps: HeaderExpandedInfoBlockProps) {
        if (this.isFetching) {
            this.moveScrollToEnd();
        }
    }

    render() {
        const { title, isOpen, onClick, moreInfoHref } = this.props;

        return (
            <div className={Bem.cls('header-expanded-info-block')}>
                <div
                    className={Bem.cls('header-expanded-info-block', 'header')}
                >
                    <div
                        onClick={onClick}
                        className={Bem.cls(
                            'header-expanded-info-block',
                            'header-title'
                        )}
                    >
                        <Shape2
                            width={11}
                            height={11}
                            cls={Bem.cls(
                                'header-expanded-info-block',
                                'arrow',
                                {
                                    opened: isOpen
                                }
                            )}
                            theme={Shape2Theme.light}
                            icon={Shape2Icon.verticalArrow}
                        />
                        {title}
                    </div>
                    {moreInfoHref && (
                        <div
                            className={Bem.cls(
                                'header-expanded-info-block',
                                'archive'
                            )}
                        >
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                href={moreInfoHref}
                                cls={Bem.cls(
                                    'header-expanded-info-block',
                                    'archive-link'
                                )}
                            >
                                {i18n('archive')}
                            </StyledLink>
                        </div>
                    )}
                </div>
                {this.getExpandedContent()}
                {this.getFooterContent()}
            </div>
        );
    }

    private getExpandedContent = () => {
        const { isOpen, children } = this.props;

        if (!isOpen) {
            return null;
        }

        return (
            <>
                {this.getErrorContent()}
                <div
                    ref={this.setRef}
                    className={Bem.cls('header-expanded-info-block', 'content')}
                >
                    {children}
                </div>
            </>
        );
    };

    private getFooterContent = () => {
        const { isOpen, fetchItems, isLoading } = this.props;

        if (!isOpen || !fetchItems) {
            return null;
        }

        return (
            <div className={Bem.cls('header-expanded-info-block', 'footer')}>
                {isLoading ? (
                    <Spin progress={true} size={'xxs'} />
                ) : (
                    <span
                        onClick={this.onFetchItems}
                        className={Bem.cls(
                            'header-expanded-info-block',
                            'load-more-text'
                        )}
                    >
                        {i18n('load')}
                    </span>
                )}
            </div>
        );
    };

    private onFetchItems = (e: MouseEvent<HTMLElement>) => {
        // TODO: Легошный баг с https://st.yandex-team.ru/ISL-5284
        e.stopPropagation();

        this.isFetching = true;

        this.props.fetchItems();
    };

    private setRef = (ref: ReactNode) => {
        this.content = ref;
    };

    private moveScrollToEnd = () => {
        if (this.content) {
            (this.content as Element).scrollTop = (this
                .content as Element).scrollHeight;
        }
    };

    private getErrorContent = () => {
        const { error } = this.props;

        if (error) {
            return (
                <Alert
                    visible={true}
                    iconTone={Shape2Tone.white}
                    className={Bem.cls(
                        'header-expanded-info-block',
                        'error-alert'
                    )}
                    type={AlertType.error}
                    size={AlertSize.s}
                >
                    {error}
                </Alert>
            );
        }

        return null;
    };
}
